import React, {useEffect, useState} from 'react';
import {connect, useDispatch} from 'react-redux';
import {bindActionCreators} from 'redux';
import {withTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import IPosModal from '../../../IPosModal';
import PaymentSwitcher from './PaymentSwitcher';
import CashPayment from './CashPayment';
import WaiterPayment from './WaiterPayment';
import CreditCardPayment from './CreditCardPayment';
import TenBisPayment from './TenBisPayment';
import SodexoPayment from './SodexoPayment';
import {PaymentType} from '../../../constants/paymentTypes';
import {clearClientInfo, updateClientInfo} from '../../../redux/actions/clientActions';
import {clearOrder, submitOrder} from '../../../redux/actions/orderActions';
import {setDeliveryType, setOrderType, setOrderTips} from '../../../redux/actions/baseActions';
import {clearCostDelivery} from '../../../redux/actions/costDeliveryActions';
import {sendConfirmationCode} from '../../../redux/actions/paymentActions';
import {DeliveryType} from '../../../constants/deliveryTypes';

import './personPaymentSelector.scss';

const PersonPaymentSelector = ({
                                 person,
                                 updatePerson,
                                 paymentTypes,
                                 clientInfo,
                                 branchInfo,
                                 t,
                                 removePerson,
                                 moreThanOnePayment,
                                 showCashPayment,
                                 currentDeliveryType,
                                 currentLanguageId,
                                 currentTableId
                               }) => {
  const dispatch = useDispatch();
  const [currentPaymentType, setCurrentPaymentType] = useState(person.paymentType);
  const [amount, setAmount] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [companyData, setCompanyData] = useState(0);

  const onChangePaymentType = (paymentType) => {
    if (paymentType && paymentType.payment_code === "CREDIT_CARD")
    {
        addTips();
    }
    setCurrentPaymentType(paymentType);    
    setCardData(null);
    setCompanyData(0);
  };

  useEffect(() => {
    person.paymentType = currentPaymentType;
    person.amount = amount ? parseFloat(amount) : null;
    person.cardData = cardData;
    person.companyCode = companyData;
    updatePerson(person);
  }, [amount, cardData, companyData, currentPaymentType]);

  useEffect(() => {
    setCurrentPaymentType(person.paymentType);
    setAmount(person.amount ? person.amount : '');
  }, [person, person.amount]);

  useEffect(() => {
    // This change is done mostly for kiosk if there is only one payment type
    // select it by default
    if (paymentTypes && paymentTypes.length === 1 && !currentPaymentType && !person.paymentType) {
      setCurrentPaymentType(paymentTypes[0]);
    }
  }, [paymentTypes, person.paymentType]);

  const addTips = () => {
    return new Promise((resolve, reject) => {
      if (currentTableId) {
        IPosModal.tipsModal({
          orderPaymentsSum: parseInt(amount * 100, 10),
          t: t,
          okButtonText: t("buttons.continue"),
          cancelButtonText: t("buttons.cancel"),
          currentLanguageId: currentLanguageId,
          onSubmit: (tipsAmount) => {
            dispatch(setOrderTips(tipsAmount));
            resolve(tipsAmount);
          },

          onClose: () => {
            setOrderTips(0);
            resolve(0);
          },
        });
      } else {
        setOrderTips(0);
        resolve(0);
      }
    });
  };

  const onChangeAmount = (e) => {
    const newAmount = e.currentTarget.value;
    setAmount(newAmount);
  };

  const creditCardPaymentAllowed = currentPaymentType && currentPaymentType.payment_code === PaymentType.CREDIT_CARD && (!branchInfo.use_pin_pad_flg || branchInfo.use_pin_pad_flg === 0);

  let paymentTypeList = paymentTypes;

    if (currentTableId && branchInfo.use_waiter_payment_flg && currentDeliveryType === DeliveryType.EAT_IN) {
        paymentTypeList = [...paymentTypes, {
            payment_code: PaymentType.WAITER,
            payment_type_name: t('payment.payment_type.waiter')
        }];
    }

    // Vitaliy's requirement - hide cash payment if table is selected
    if (currentTableId) {
       paymentTypeList = paymentTypeList.filter(paymentType => paymentType.payment_code !== PaymentType.CASH);
    }

  return (
    <div className={ 'person_payment_selector' }>
      <PaymentSwitcher
        key={ `${person.uuid}switcher` }
        currentPaymentType={ currentPaymentType }
        setCurrentPaymentType={ onChangePaymentType }
        paymentTypes={ paymentTypeList }
      />

      {currentPaymentType && currentPaymentType.payment_code === PaymentType.WAITER && (
        <WaiterPayment key={ `${person.uuid}waiter` } branchInfo={ branchInfo } />
      )}

      {currentPaymentType && currentPaymentType.payment_code === PaymentType.CASH && (
        <CashPayment key={ `${person.uuid}cash` } clientInfo={ clientInfo } branchInfo={ branchInfo } />
      )}

      {creditCardPaymentAllowed && branchInfo.is_zcreditwc_flg !== 1
        && (
          <CreditCardPayment key={ `${person.uuid}cc` } setCardData={ setCardData } />
        )}

      {creditCardPaymentAllowed && branchInfo.is_zcreditwc_flg === 1
        && (
              <div className={ 'payment-block credit-card-payment' }>
                  <div className={ 'payment-content' }>
                      <div className="pin-pad-pay-note">{t('payment.credit_card_pay_note')}</div>
                  </div>
              </div>
          )}

        {currentPaymentType
            && currentPaymentType.payment_code === PaymentType.CREDIT_CARD
            && branchInfo.use_pin_pad_flg === 1 && (
                <div className={ 'payment-block credit-card-payment' }>
                    <div className={ 'payment-content' }>
              <div className={ 'pin-pad-pay-note' }>{t('payment.pin_pad_pay_note')} </div>
            </div>
          </div>
        )}

      {currentPaymentType && currentPaymentType.payment_code === PaymentType.TEN_BIS && (
        <TenBisPayment
          key={ `${person.uuid}tenbis` }
          setCardData={ setCardData }
        />
      )}

      {currentPaymentType && currentPaymentType.payment_code === PaymentType.CIBUS && (
        <SodexoPayment 
          key={ person.uuid } 
          setCardData={ setCardData }
          setCompanyData = { setCompanyData }
        />
      )}

      {!currentPaymentType && (
        <div className={ 'payment-block nothing-selected' }>
          {/*<h2>{ t('payment.payment_method') }</h2>*/}
          <div className={ 'payment-content' }>
            {t('payment.select_payment_description')}
          </div>
        </div>
      )}

      {moreThanOnePayment && (
        <div className={ 'person-amount-block' }>
          <div className={ 'form-input required' }>
            <label htmlFor="person_amount">{t('payment.amount')}</label>
            <input
              className={ 'person_amount' }
              type={ 'number' }
              name={ 'person_amount' }
              value={ amount }
              onChange={ onChangeAmount }
              placeholder={ t('payment.amount_placeholder') }
            />
          </div>

          {!showCashPayment && (
            <button
              className={ 'btn ' }
              onClick={ () => {
                removePerson(person);
              } }
            >
              <FontAwesomeIcon icon="trash" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
const mapStateToProps = (state) => {
  const {
    clientInfo,
    branchInfo,
    paymentTypes,
    currentTableId,
    currentLanguageId,
    currentDeliveryType
  } = state;

  return {
    clientInfo,
    branchInfo,
    paymentTypes,
    currentTableId,
    currentLanguageId,
    currentDeliveryType
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateClientInfo,
      submitOrder,
      clearOrder,
      setOrderTips,
      setDeliveryType,
      setOrderType,
      clearClientInfo,
      clearCostDelivery,
      sendConfirmationCode
    },
    dispatch,
  );

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(PersonPaymentSelector));
